/* Retrowave V17: use one serialized controller for drawer quantities, removal and shipping protection.
   Preserve Shrine native quantity, cart design, product buttons and settings. */
(() => {
  'use strict';

  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const drawer = () => document.querySelector('cart-drawer');
  const insurancePattern = /shipping[\s\-_]*(?:protection|insurance)/i;
  const optedOut = new Set();
  const storageKey = 'retrowave:cart-protection-opt-outs';
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    if (Array.isArray(saved)) saved.forEach(key => optedOut.add(String(key)));
  } catch (_) { /* Storage is optional. */ }

  function insuranceWidget(el) {
    if (!el || !el.matches?.('cart-drawer-upsell[data-toggle="true"]')) return false;
    return insurancePattern.test(el.dataset.handle || '') ||
      insurancePattern.test(el.querySelector('.upsell__title')?.textContent || '');
  }
  const insuranceKey = el => String(el?.dataset.handle || 'shipping-protection');
  function persistOptOut(key, value) {
    if (value) optedOut.add(key);
    else optedOut.delete(key);
    try { sessionStorage.setItem(storageKey, JSON.stringify([...optedOut])); }
    catch (_) { /* Optional persistence must never block a toggle. */ }
  }
  function optedOutFor(el) { return insuranceWidget(el) && optedOut.has(insuranceKey(el)); }

  // Shrine re-renders the upsell from its initial "preselected" Liquid settings
  // after each cart change. Without remembering an opt-out it re-adds the
  // shipping-protection product that the customer just removed.
  function installProtectionGuard() {
    const ctor = customElements.get('cart-drawer-upsell');
    if (!ctor || ctor.prototype.__retrowaveGuardInstalled) return;
    const proto = ctor.prototype;
    proto.__retrowaveGuardInstalled = true;
    if (typeof proto.getUpdateRequired === 'function') {
      const original = proto.getUpdateRequired;
      proto.getUpdateRequired = function (...args) {
        if (insuranceWidget(this)) {
          if (optedOutFor(this)) this.dataset.selected = 'false';
          // Only Retrowave's serialized protection controller mutates this SKU.
          // Shrine's implicit preselected auto-add would otherwise race deletes.
          return false;
        }
        return original.apply(this, args);
      };
    }
    if (typeof proto.addToCart === 'function') {
      const originalAdd = proto.addToCart;
      proto.addToCart = function (...args) {
        if (insuranceWidget(this)) return;
        return originalAdd.apply(this, args);
      };
    }
  }
  installProtectionGuard();
  customElements.whenDefined('cart-drawer-upsell').then(installProtectionGuard).catch(() => {});

  function otherModalOpen() {
    return [...document.querySelectorAll('quick-add-modal, modal-dialog, details-modal')]
      .some(el => el.hasAttribute('open') || el.classList.contains('active'));
  }
  function releaseScrollLock() {
    if (!drawer()?.classList.contains('active') && !otherModalOpen()) {
      document.body.classList.remove('overflow-hidden');
    }
  }
  function closeDrawer(cart) {
    if (!cart) return;
    try { if (typeof cart.close === 'function') cart.close(); }
    catch (error) { console.warn('[Retrowave] Cart close fallback:', error); }
    finally {
      cart.classList.remove('active');
      releaseScrollLock();
      requestAnimationFrame(releaseScrollLock);
    }
  }

  // Delegation is attached to document, not #CartDrawer-Overlay or drawer close
  // nodes: Shopify section rendering replaces those nodes on each add/remove.
  document.addEventListener('click', event => {
    const cart = drawer();
    if (!cart) return;
    const inside = event.target.closest('cart-drawer');
    if (inside && (event.target.closest('#CartDrawer-Overlay') ||
                   event.target.closest('.drawer__close') ||
                   event.target.closest('.retrowave-cart-empty__continue'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDrawer(cart);
      return;
    }
    // Native Shrine binds only the tiny #cart-icon-bubble, not the whole icon.
    // Rebind the whole header cart anchor without producing another drawer.
    const trigger = event.target.closest('a.header__icon--cart');
    if (trigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (typeof cart.open === 'function') {
        try { cart.open(trigger); }
        catch (err) {
          console.warn('[Retrowave] Native cart open failed:', err);
          location.assign(trigger.href);
        }
      } else location.assign(trigger.href);
    }
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !drawer()?.classList.contains('active')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeDrawer(drawer());
  }, true);

  // If the theme closes the drawer programmatically, recover the body lock.
  function watchDrawer() {
    const cart = drawer();
    if (!cart || cart.__retrowaveScrollObserver) return;
    let wasOpen = cart.classList.contains('active');
    const observer = new MutationObserver(() => {
      const isOpen = cart.classList.contains('active');
      if (!isOpen) releaseScrollLock();
      else if (!wasOpen) scheduleProtectionReconcile();
      wasOpen = isOpen;
    });
    observer.observe(cart, {attributes: true, attributeFilter: ['class']});
    // On Shopify section replacement, keep intentional insurance opt-outs
    // visually switched off. Do not start additional cart API requests on
    // every DOM mutation -- Shrine is already handling the quantity update.
    const body = cart.querySelector('#CartDrawer');
    if (body) {
      const contentObserver = new MutationObserver(() => {
        body.querySelectorAll('cart-drawer-upsell[data-toggle="true"]').forEach(el => {
          if (optedOutFor(el)) el.dataset.selected = 'false';
        });
      });
      contentObserver.observe(body, {childList: true, subtree: true});
      cart.__retrowaveProtectionObserver = contentObserver;
    }
    cart.__retrowaveScrollObserver = observer;
    if (cart.classList.contains('active')) scheduleProtectionReconcile();
  }

  async function requestJson(path, options) {
    const response = await fetch(root() + path, {credentials: 'same-origin', ...options});
    const data = await response.json();
    if (!response.ok || (data.status && data.status >= 400))
      throw new Error(data.description || data.message || `Cart HTTP ${response.status}`);
    return data;
  }
  const sectionsRequest = {
    sections: ['cart-drawer', 'cart-icon-bubble'],
    sections_url: location.pathname
  };
  const jsonPost = (path, payload) => requestJson(path, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
    body: JSON.stringify({...payload, ...sectionsRequest})
  });
  // All writes introduced by this repair share one queue. Shopify Ajax
  // cart endpoints are not transactional when simultaneous updates race.
  // Shrine's own +/- handling remains untouched outside a removal operation.
  let mutationQueue = Promise.resolve();
  let deletingLine = false;
  let quantityBusy = false;
  function enqueueCartWrite(task) {
    const current = mutationQueue.catch(() => {}).then(task);
    mutationQueue = current.catch(() => {});
    return current;
  }

  // A checked preselected protection switch does NOT guarantee the
  // protection SKU is in Shopify's cart. Never fake the subtotal by adding
  // $2.99 in the DOM: reconcile the actual item, then re-render the server
  // cart.total_price so cart display and checkout agree.
  let reconciliationRunning = false;
  let reconcileQueued = false;
  let protectionReconcileSuppressed = false;
  function activeProtectionWidget() {
    const cart = drawer();
    if (!cart?.classList.contains('active')) return null;
    return [...cart.querySelectorAll('cart-drawer-upsell[data-toggle="true"]')]
      .find(el => insuranceWidget(el) && el.dataset.selected === 'true' && !optedOutFor(el));
  }
  function reconcileProtection() {
    if (reconciliationRunning || deletingLine || protectionReconcileSuppressed || busyProtection.size) return;
    return enqueueCartWrite(async () => {
    if (reconciliationRunning || deletingLine || protectionReconcileSuppressed || busyProtection.size) return;
    const widget = activeProtectionWidget();
    if (!widget) return;
    const id = Number(widget.dataset.id);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    reconciliationRunning = true;
    const total = drawer()?.querySelector('.cart-drawer__totals');
    if (total) total.setAttribute('aria-busy', 'true');
    try {
      const current = await requestJson('cart.js');
      const protectionLines = current.items.filter(item => Number(item.variant_id || item.id) === id);
      // Restore existing incorrect quantities to one without adding more insurance.
      if (protectionLines.length && activeProtectionWidget()) {
        const incorrect = protectionLines.some((item, index) => item.quantity !== (index === 0 ? 1 : 0));
        if (incorrect) {
          let corrected = current;
          for (let index = 0; index < protectionLines.length; index++) {
            const item = protectionLines[index];
            const target = index === 0 ? 1 : 0;
            if (item.quantity !== target) corrected = await jsonPost('cart/change.js', {id: item.key, quantity: target});
          }
          await fetchCartSections(drawer(), corrected);
        }
        return;
      }
      // Shipping protection should never become the only product in an empty cart.
      const hasMainProduct = current.items.some(item => Number(item.variant_id || item.id) !== id);
      if (!hasMainProduct || !activeProtectionWidget()) return;
      // Adds the real protection variant, not a cosmetic surcharge.
      const result = await jsonPost('cart/add.js', {items: [{id, quantity: 1}]});
      const updated = await requestJson('cart.js');
      result.item_count = updated.item_count;
      await fetchCartSections(drawer(), result);
    } catch (err) {
      // Don't loop indefinitely when inventory, a 3rd-party app, or the
      // Shopify Ajax endpoint rejects the insurance line item.
      protectionReconcileSuppressed = true;
      console.warn('[Retrowave] Cannot include protection in cart subtotal:', err);
      const liveWidget = activeProtectionWidget();
      if (liveWidget) liveWidget.dataset.selected = 'false';
    } finally {
      reconciliationRunning = false;
      drawer()?.querySelector('.cart-drawer__totals')?.removeAttribute('aria-busy');
    }
    });
  }
  function scheduleProtectionReconcile() {
    if (reconcileQueued || reconciliationRunning) return;
    reconcileQueued = true;
    // Let Shrine's native upsell initialization complete before fetching.
    setTimeout(() => {
      reconcileQueued = false;
      void reconcileProtection();
    }, 180);
  }
  function renderCartResponse(cart, result) {
    if (!result.sections?.['cart-drawer']) throw new Error('Missing cart-drawer response');
    try {
      if (typeof cart.renderContents !== 'function') throw new Error('Missing cart renderer');
      // true means: refresh content without reopening/re-focusing an open drawer.
      cart.renderContents(result, true);
    } catch (err) {
      const dom = new DOMParser().parseFromString(result.sections['cart-drawer'], 'text/html');
      const incoming = dom.querySelector('#CartDrawer');
      const existing = cart.querySelector('#CartDrawer');
      if (!incoming || !existing) throw err;
      existing.innerHTML = incoming.innerHTML;
      const bubble = new DOMParser().parseFromString(result.sections['cart-icon-bubble'] || '', 'text/html')
        .querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (bubble && currentBubble) currentBubble.innerHTML = bubble.innerHTML;
    }
    cart.classList.toggle('is-empty', result.item_count === 0);
    if (result.item_count > 0) cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
    // In the native renderer, a zero-delay callback initializes the new nodes.
    // The opted-out flag above prevents auto-adding removed insurance again.
    cart.querySelectorAll('cart-drawer-upsell[data-toggle="true"]').forEach(el => {
      if (optedOutFor(el)) el.dataset.selected = 'false';
    });
    scheduleProtectionReconcile();
  }
  async function fetchCartSections(cart, result) {
    if (!result.sections?.['cart-drawer']) {
      const response = await fetch(root() + 'cart?sections=cart-drawer,cart-icon-bubble', {credentials: 'same-origin'});
      if (!response.ok) throw new Error('Cannot refresh cart sections');
      result.sections = await response.json();
    }
    renderCartResponse(cart, result);
  }
  const busyProtection = new Set();
  async function toggleProtection(widget) {
    const key = insuranceKey(widget);
    protectionReconcileSuppressed = false;
    if (busyProtection.has(key)) return;
    busyProtection.add(key);
    return enqueueCartWrite(async () => {
    const cart = drawer();
    const previousSelection = widget.dataset.selected;
    const previousOptOut = optedOut.has(key);
    // If the switch looks off, add; otherwise remove. The preselected state
    // is corrected after each cart refresh when a customer has opted out.
    const shouldEnable = widget.dataset.selected !== 'true';
    const toggle = widget.querySelector('.upsell-toggle-btn');
    if (toggle) toggle.setAttribute('aria-busy', 'true');
    widget.dataset.selected = String(shouldEnable);
    persistOptOut(key, !shouldEnable);
    try {
      const current = await requestJson('cart.js');
      const matches = current.items.filter(item => item.handle === key ||
        (insurancePattern.test(item.product_title || '') && insurancePattern.test(key)));
      let result = null;
      if (shouldEnable) {
        if (!matches.length) {
          const id = Number(widget.dataset.id || widget.querySelector('input[name="id"]')?.value);
          if (!id) throw new Error('Shipping protection variant ID missing');
          result = await jsonPost('cart/add.js', {items: [{id, quantity: 1}]});
          // /cart/add.js returns line items, not a total cart object.
          const refreshed = await requestJson('cart.js');
          result.item_count = refreshed.item_count;
        } else {
          result = current;
        }
      } else {
        // Cart line-item keys are safer than positions/variant IDs for removal.
        if (matches.length) {
          for (const item of matches) {
            result = await jsonPost('cart/change.js', {id: item.key, quantity: 0});
          }
        } else {
          result = current;
        }
      }
      if (!result) result = await requestJson('cart.js');
      await fetchCartSections(cart, result);
    } catch (err) {
      widget.dataset.selected = previousSelection;
      persistOptOut(key, previousOptOut);
      console.error('[Retrowave] Shipping protection toggle failed:', err);
      // Do not silently claim an insurance removal succeeded.
      const notice = drawer()?.querySelector('#CartDrawer-CartErrors');
      if (notice) notice.textContent = 'Could not update shipping protection.';
    } finally {
      busyProtection.delete(key);
      if (toggle?.isConnected) toggle.removeAttribute('aria-busy');
    }
    });
  }

  // Shrine's <cart-remove-button> removes by a cart LINE NUMBER. When
  // preselected shipping protection changes the cart concurrently, that index
  // can refer to a different item or a removed line. Delete by unique item.key
  // instead, with the insurance mutations serialized through the same queue.
  function removeError(message) {
    const target = drawer()?.querySelector('#CartDrawer-CartErrors');
    if (target) target.textContent = message || '';
  }
  async function removeCartLine(row, key) {
    if (!key || deletingLine || quantityBusy) return;
    deletingLine = true;
    const button = row.querySelector('cart-remove-button button');
    if (button) button.disabled = true;
    row.setAttribute('aria-busy', 'true');
    removeError('');
    try {
      await enqueueCartWrite(async () => {
        // Read server state first; never guess which product a stale index is.
        const before = await requestJson('cart.js');
        if (!before.items.some(item => item.key === key)) {
          await fetchCartSections(drawer(), before);
          return;
        }
        let after = await jsonPost('cart/change.js', {id: key, quantity: 0});
        if (after?.errors || !Array.isArray(after?.items)) {
          throw new Error(after?.description || 'Shopify did not confirm the removal');
        }
        // Remove any remaining insurance only if no merchandise remains.
        // Otherwise keep the opted-in protection exactly as it was.
        if (!after.items.some(item => !insurancePattern.test(item.handle || '') &&
          !insurancePattern.test(item.product_title || ''))) {
          const insuranceItems = after.items.filter(item => insurancePattern.test(item.handle || '') ||
            insurancePattern.test(item.product_title || ''));
          for (const item of insuranceItems) {
            after = await jsonPost('cart/change.js', {id: item.key, quantity: 0});
          }
        }
        // Render the current server cart, not the response of a stale request.
        // /cart/change.js embedded sections can predate a concurrent app update.
        const current = await requestJson('cart.js');
        if (current.items.some(item => item.key === key)) {
          throw new Error('Item is still in the cart; please try again');
        }
        await fetchCartSections(drawer(), current);
      });
    } catch (error) {
      console.error('[Retrowave] Cart line removal failed:', error);
      // Refresh even after an error: the server might have accepted the change
      // before a section-rendering request failed.
      try {
        const current = await requestJson('cart.js');
        await fetchCartSections(drawer(), current);
        if (current.items.some(item => item.key === key)) {
          removeError(error.message || 'Could not remove this item. Please try again.');
        }
      } catch (refreshError) {
        console.warn('[Retrowave] Cart refresh after removal failed:', refreshError);
        removeError('Cart could not be refreshed. Please reload the page.');
      }
    } finally {
      deletingLine = false;
      if (row.isConnected) {
        row.removeAttribute('aria-busy');
        if (button) button.disabled = false;
      }
    }
  }
  document.addEventListener('click', event => {
    const control = event.target.closest('cart-drawer-items cart-remove-button');
    if (!control) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (deletingLine || quantityBusy) return;
    const row = control.closest('.cart-drawer-item[data-key]');
    const key = control.dataset.key || row?.dataset.key;
    if (!row || !key) {
      removeError('Could not identify the item. Please refresh the cart.');
      return;
    }
    void removeCartLine(row, key);
  }, true);
  // V17: One writer for all drawer quantity changes. Catch clicks in capture
  // BEFORE Shrine's quantity-input custom element or CartItems change handler
  // runs. Always address an exact Shopify cart item key (never line number).
  // Deletion and shipping-protection writes use the same promise queue.
  function quantityRow(element) {
    const row = element?.closest?.('cart-drawer-items .cart-drawer-item[data-key]');
    if (!row) return null;
    return row;
  }
  function isProtectionRow(row) {
    return !!row && insurancePattern.test(row.dataset.handle || '');
  }
  function quantityInput(row) {
    return row?.querySelector('input.quantity__input[name="updates[]"]');
  }
  function boundedQuantity(raw, input) {
    const number = Number(raw);
    if (!Number.isFinite(number)) return null;
    const minimum = Math.max(1, Number(input.min) || 1);
    const maximum = input.max !== '' ? Number(input.max) : Infinity;
    const increment = Math.max(1, Number(input.step) || 1);
    let target = Math.max(minimum, Math.round(number));
    if (Number.isFinite(maximum)) target = Math.min(maximum, target);
    if (increment !== 1) {
      target = minimum + Math.floor((target - minimum) / increment) * increment;
    }
    return target;
  }
  function messageCartError(message, row) {
    const detail = row?.querySelector('.cart-item__error-text');
    if (detail) detail.textContent = message || '';
    const general = drawer()?.querySelector('#CartDrawer-CartErrors');
    if (general) general.textContent = message || '';
  }
  async function updateDrawerQuantity(row, desired) {
    if (!row || !row.isConnected || deletingLine || quantityBusy) return;
    const input = quantityInput(row);
    const key = row.dataset.key;
    if (!input || !key) return;
    const target = boundedQuantity(desired, input);
    const oldValue = Number(row.dataset.quantity || input.defaultValue || input.value);
    if (target === null || target === oldValue) {
      input.value = String(oldValue);
      return;
    }
    if (isProtectionRow(row)) {
      input.value = String(oldValue);
      return;
    }
    quantityBusy = true;
    row.setAttribute('aria-busy', 'true');
    row.querySelectorAll('.quantity__button').forEach(button => { button.disabled = true; });
    input.disabled = true;
    // Give immediate feedback without guessing money totals. The Shopify
    // server is authoritative; a rejected change is rolled back below.
    input.value = String(target);
    messageCartError('', row);
    // An accepted Shopify update is not a failure just because its line key
    // changes after discounts or apps recompute the cart.
    let changeAccepted = false;
    try {
      await enqueueCartWrite(async () => {
        const changed = await jsonPost('cart/change.js', {id: key, quantity: target});
        if (!Array.isArray(changed.items) || changed.errors) {
          throw new Error(changed.description || changed.message || 'Cart quantity was not updated');
        }
        changeAccepted = true;
        // Shopify bundled section rendering normally gives the authoritative
        // drawer HTML with the change request: one network round trip.
        // Some app integrations return stale/missing HTML; in that case retain
        // V17's safe refresh path rather than displaying an incorrect cart.
        let canRenderFast = false;
        const markup = changed.sections?.['cart-drawer'];
        if (typeof markup === 'string' && markup.includes('CartDrawer')) {
          const snapshot = new DOMParser().parseFromString(markup, 'text/html');
          const matchingRow = Array.from(snapshot.querySelectorAll('.cart-drawer-item[data-key]'))
            .find(element => element.dataset.key === key);
          canRenderFast = !!matchingRow && Number(matchingRow.dataset.quantity) === target;
        }
        if (canRenderFast) {
          await fetchCartSections(drawer(), changed);
        } else {
          const refreshed = await requestJson('cart.js');
          await fetchCartSections(drawer(), refreshed);
        }
      });
    } catch (error) {
      // A cart update may succeed while a subsequent section render fails.
      // Re-read the authoritative cart before showing any customer-facing error.
      console.warn('[Retrowave] Quantity update or cart refresh needs checking:', error);
      let serverConfirmed = false;
      let currentQuantity = null;
      try {
        const refreshed = await requestJson('cart.js');
        const exact = refreshed.items?.find(item => item.key === key);
        const variantId = Number(input.dataset.quantityVariantId ||
          row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId);
        const candidates = Number.isSafeInteger(variantId) && variantId > 0
          ? refreshed.items?.filter(item => Number(item.variant_id) === variantId) || []
          : [];
        // Only use a variant fallback if it uniquely identifies the line.
        const actual = exact || (candidates.length === 1 ? candidates[0] : null);
        if (actual) {
          currentQuantity = Number(actual.quantity);
          serverConfirmed = currentQuantity === target;
        }
        try { await fetchCartSections(drawer(), refreshed); }
        catch (refreshError) {
          console.warn('[Retrowave] Cart display refresh failed:', refreshError);
        }
      } catch (refreshError) {
        console.warn('[Retrowave] Could not verify updated cart:', refreshError);
      }
      if (changeAccepted || serverConfirmed) {
        // Successful updates must never display an error just because a
        // third-party script changed the line key or the HTML was stale.
        messageCartError('', row);
        if (row.isConnected && Number.isFinite(currentQuantity)) {
          input.value = String(currentQuantity);
        }
      } else {
        // Only a real, unconfirmed update gets one brief inline message.
        messageCartError(error.message || 'Could not update cart. Try again.', row);
        if (row.isConnected) input.value = String(Number.isFinite(currentQuantity) ? currentQuantity : oldValue);
      }
    } finally {
      quantityBusy = false;
      if (row.isConnected) {
        row.removeAttribute('aria-busy');
        row.querySelectorAll('.quantity__button').forEach(button => { button.disabled = false; });
        input.disabled = false;
      }
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('cart-drawer-items .quantity__button');
    if (!button) return;
    const row = quantityRow(button);
    if (!row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (quantityBusy || deletingLine || isProtectionRow(row)) return;
    const input = quantityInput(row);
    if (!input) return;
    const oldValue = Number(row.dataset.quantity || input.defaultValue || input.value);
    const increment = Math.max(1, Number(input.step) || 1);
    const isMinus = button.name === 'minus' || button.classList.contains('quantity__button--minus');
    void updateDrawerQuantity(row, oldValue + (isMinus ? -increment : increment));
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest?.('cart-drawer-items input.quantity__input[name="updates[]"]');
    if (!input) return;
    const row = quantityRow(input);
    if (!row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (quantityBusy || deletingLine || isProtectionRow(row)) {
      input.value = String(row.dataset.quantity || input.defaultValue || '1');
      return;
    }
    void updateDrawerQuantity(row, input.value);
  }, true);

  // A cart-drawer-upsell can be rebuilt by every Ajax render. Capture only the
  // exact protection toggle; all other upsells retain native Shrine handlers.
  document.addEventListener('click', event => {
    const widget = event.target.closest('cart-drawer-upsell[data-toggle="true"]');
    if (!insuranceWidget(widget)) return;
    const control = event.target.closest('.upsell-toggle-btn');
    if (!control || !widget.contains(control)) return;
    if (event.target.closest('select, option, a')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    toggleProtection(widget);
  }, true);

  // Initialize only after every closure-scoped state variable has been declared.
  // A drawer that starts "active" otherwise accessed reconcileQueued in TDZ.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer, {once: true});
  else watchDrawer();
  document.addEventListener('shopify:section:load', watchDrawer);
})();
